import React from 'react';
import classNames from 'classnames';
import { TableProps } from './Table.types';
import TableHeader from './TableHeader';
import TableRow from './TableRow';

/**
 * Enhanced Table component that provides a standardized, responsive
 * table with sorting, loading states, and consistent styling.
 */
export const Table = <T extends {}>({
  data = [],
  columns,
  rowKey,
  loading = false,
  emptyMessage = 'No data available',
  striped = false,
  hoverable = true,
  fixedHeader = false,
  maxHeight,
  sortBy,
  sortDirection,
  onSortChange,
  onRowClick,
  className,
  ...rest
}: TableProps<T>) => {
  // Generate a key for each row
  const getRowKey = (row: T, index: number): string | number => {
    if (rowKey) {
      return rowKey(row);
    }
    
    // Try to use common ID fields
    if ((row as any).id) {
      return (row as any).id;
    }
    
    if ((row as any)._id) {
      return (row as any)._id;
    }
    
    // Fallback to index
    return index;
  };
  
  return (
    <div className={classNames('w-full overflow-x-auto', className)}>
      <table className="min-w-full text-sm text-gray-800 dark:text-gray-100" {...rest}>
        <TableHeader 
          columns={columns}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
          fixed={fixedHeader}
        />
        
        <tbody 
          className={classNames('bg-white/85 dark:bg-slate-900/75', { 'border-t border-gray-200 dark:border-white/10': !fixedHeader })}
          style={maxHeight && fixedHeader ? { maxHeight, overflowY: 'auto' } : undefined}
        >
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-600 dark:text-gray-300">
                <div className="flex justify-center items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-100" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Loading...</span>
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-600 dark:text-gray-300">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <TableRow
                key={getRowKey(row, index)}
                data={row}
                columns={columns}
                index={index}
                striped={striped}
                hoverable={hoverable}
                onClick={onRowClick}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;